import { Suspense, ComponentType, ReactNode } from 'react';
import { PageLoader } from '@/components/ui/PageLoader';
import { ViewLoadingFallback } from '@/components/ui/ViewLoadingFallback';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { ErrorBoundary } from '@/components/ErrorBoundary';

interface LazyRouteOptions {
  fallbackMessage?: string;
  moduleName?: string;
  fallback?: ReactNode;
}

/**
 * Helper to create lazy-loaded route components with Suspense + ErrorBoundary.
 * Each route gets its own error boundary so a crash in one page doesn't take
 * down the entire app — the user sees an error UI with a reload button while
 * navigation to other pages still works.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function lazyRoute<T extends ComponentType<Record<string, unknown>>>(
  importFn: () => Promise<{ default: T }>,
  fallbackMessageOrOptions?: string | LazyRouteOptions,
  moduleName?: string
) {
  const options: LazyRouteOptions =
    typeof fallbackMessageOrOptions === 'string' || fallbackMessageOrOptions === undefined
      ? { fallbackMessage: fallbackMessageOrOptions, moduleName }
      : fallbackMessageOrOptions;

  const LazyComponent = lazyWithRetry(importFn, options.moduleName || options.fallbackMessage);
  const fallbackElement = options.fallback ?? <PageLoader message={options.fallbackMessage} />;

  return function LazyRoute(props: Record<string, unknown>) {
    return (
      <ErrorBoundary>
        <Suspense fallback={fallbackElement}>
          <div className="animate-page-in">
            {/* @ts-expect-error - LazyComponent props are dynamic */}
            <LazyComponent {...props} />
          </div>
        </Suspense>
      </ErrorBoundary>
    );
  };
}

// Re-export so callers can pass custom fallbacks without a separate import
export { ViewLoadingFallback };

/**
 * Pre-defined lazy routes for main pages
 * Import these in your router configuration
 */

// Main app (Index contains views: Dashboard, CRM, Actions, etc.)
// Uses app-shaped skeleton instead of spinner — matches the real layout so the
// transition from skeleton to loaded app feels instant instead of jarring.
export const LazyIndex = lazyRoute(
  () => import('@/pages/Index'),
  { fallbackMessage: 'Loading app...', fallback: <ViewLoadingFallback /> }
);

export const LazyCalculatorDraftEditor = lazyRoute(
  () => import('@/pages/CalculatorDraftEditor'),
  'Loading calculator editor...'
);

export const LazyTrackingGoRedirect = lazyRoute(
  () => import('@/pages/TrackingGoRedirect'),
  'Redirecting...'
);

export const LazyPublicCalculator = lazyRoute(
  () => import('@/pages/PublicCalculator'),
  'Loading calculator...'
);

// Auth
export const LazyAuth = lazyRoute(
  () => import('@/pages/Auth'),
  'Loading authentication...'
);

// Admin pages
export const LazyAdmin = lazyRoute(
  () => import('@/pages/Admin'),
  'Loading admin...'
);

export const LazyAdminLogin = lazyRoute(
  () => import('@/pages/AdminLogin'),
  'Loading admin login...'
);

export const LazyAdminPrompts = lazyRoute(
  () => import('@/pages/AdminPrompts').then(m => ({ default: m.AdminPrompts })),
  'Loading admin prompts...'
);

export const LazyAIQualityDashboard = lazyRoute(
  () => import('@/pages/admin/AIQualityDashboard'),
  'Loading AI quality dashboard...'
);

// Accept Invitation
export const LazyAcceptInvitation = lazyRoute(
  () => import('@/pages/AcceptInvitation'),
  'Loading invitation...'
);

// Accept Partner Invitation
export const LazyAcceptPartnerInvitation = lazyRoute(
  () => import('@/pages/AcceptPartnerInvitation'),
  'Loading partner invitation...'
);

// Testimonial Response (public)
export const LazyTestimonialResponse = lazyRoute(
  () => import('@/pages/TestimonialResponse'),
  'Loading...'
);

// Public video player (VSL / nurture)
export const LazyVideoPlayer = lazyRoute(
  () => import('@/pages/VideoPlayer'),
  'Loading...'
);

export const LazyBugReportFeedback = lazyRoute(
  () => import('@/pages/BugReportFeedback'),
  'Loading...'
);

// Payment Success
export const LazyPaymentSuccess = lazyRoute(
  () => import('@/pages/PaymentSuccess'),
  'Processing payment...'
);

// Wiki
export const LazyWiki = lazyRoute(
  () => import('@/pages/Wiki'),
  'Loading wiki...'
);

export const LazyWikiArticle = lazyRoute(
  () => import('@/pages/WikiArticle'),
  'Loading article...'
);

// Possible Duplicates
export const LazyPossibleDuplicates = lazyRoute(
  () => import('@/pages/PossibleDuplicates'),
  'Loading duplicates...'
);

export const LazyValueAuditReview = lazyRoute(
  () => import('@/pages/ValueAuditReview'),
  'Loading audit review...'
);

// Zapier
export const LazyZapierAuthorize = lazyRoute(
  () => import('@/pages/ZapierAuthorize'),
  'Loading Zapier authorization...'
);

// OAuth Consent (Supabase OAuth 2.1 Server)
export const LazyOAuthConsent = lazyRoute(
  () => import('@/pages/OAuthConsent'),
  'Loading authorization...'
);

// Connected Apps (outbound MCP)
export const LazyConnectedApps = lazyRoute(
  () => import('@/pages/ConnectedApps'),
  'Loading connected apps...'
);

// Email OAuth Callback
export const LazyEmailOAuthCallback = lazyRoute(
  () => import('@/pages/EmailOAuthCallback'),
  'Connecting email account...'
);

export const LazyIntegrationOAuthCallback = lazyRoute(
  () => import('@/pages/IntegrationOAuthCallback'),
  'Connecting account...'
);

// Conversational Onboarding (authenticated)
export const LazyOnboardChat = lazyRoute(
  () => import('@/pages/OnboardChat'),
  'Setting up your workspace...'
);

// AI Demo (public, no auth)
export const LazyAIDemo = lazyRoute(
  () => import('@/pages/AIDemo'),
  'Loading AI demo...'
);

// Demo Preview (chromeless app view for iframe embedding in AI demo)
export const LazyDemoPreview = lazyRoute(
  () => import('@/pages/DemoPreview'),
  'Loading preview...'
);

// Demo Share Status (public, no auth — champion dashboard)
export const LazyDemoShareStatus = lazyRoute(
  () => import('@/pages/DemoShareStatus'),
  'Loading...'
);

export const LazySharedConversation = lazyRoute(
  () => import('@/pages/SharedConversation'),
  'Loading...'
);

export const LazyPublicDealRoom = lazyRoute(
  () => import('@/pages/PublicDealRoom'),
  'Getting your information ready...'
);

export const LazyPublicDemoWalkthrough = lazyRoute(
  () => import('@/pages/PublicDemoWalkthrough'),
  'Loading demo...'
);

// Deal Review
export const LazyDealReview = lazyRoute(
  () => import('@/components/deal-review/DealReviewPage'),
  'Loading deal review...'
);

// Coaching Canvases
export const LazyMeetingPrep = lazyRoute(
  () => import('@/components/coaching/MeetingPrepPage'),
  'Loading meeting prep...'
);

export const LazyPipelineReview = lazyRoute(
  () => import('@/components/coaching/PipelineReviewPage'),
  'Loading pipeline review...'
);

export const LazyProposalTeardown = lazyRoute(
  () => import('@/components/coaching/ProposalTeardownPage'),
  'Loading proposal teardown...'
);

export const LazyAccountExpansion = lazyRoute(
  () => import('@/components/coaching/AccountExpansionPage'),
  'Loading account expansion...'
);

export const LazyWinLoss = lazyRoute(
  () => import('@/components/coaching/WinLossPage'),
  'Loading win/loss review...'
);

export const LazyClientHealth = lazyRoute(
  () => import('@/components/coaching/ClientHealthPage'),
  'Loading client health check...'
);

export const LazyCompetitive = lazyRoute(
  () => import('@/components/coaching/CompetitivePage'),
  'Loading competitive situation room...'
);

export const LazyBusinessPlanning = lazyRoute(
  () => import('@/components/coaching/BusinessPlanningPage'),
  'Loading business planning...'
);

export const LazyQbrPrep = lazyRoute(
  () => import('@/components/coaching/QbrPrepPage'),
  'Loading QBR prep...'
);

export const LazyReferralNetwork = lazyRoute(
  () => import('@/components/coaching/ReferralNetworkPage'),
  'Loading referral network...'
);

export const LazyAbmStrategist = lazyRoute(
  () => import('@/components/coaching/AbmStrategistPage'),
  'Loading ABM strategist...'
);

export const LazyActivityHabits = lazyRoute(
  () => import('@/components/coaching/ActivityHabitsPage'),
  'Loading activity habits review...'
);


// Authority Reports (admin — behind auth)
export const LazyAuthorityReports = lazyRoute(
  () => import('@/pages/AuthorityReports'),
  'Loading authority reports...'
);

// Public Authority Report (public, no auth)
export const LazyPublicAuthorityReport = lazyRoute(
  () => import('@/pages/PublicAuthorityReport'),
  'Loading your authority report...'
);

// Public Prospect Brief (public, no auth — sent by a consultant to their prospect)
export const LazyPublicCompanyBrief = lazyRoute(
  () => import('@/pages/PublicCompanyBrief'),
  'Loading brief...'
);

// Public Authority Scanner (public, no auth — viral lead magnet)
export const LazyAuthorityScan = lazyRoute(
  () => import('@/pages/AuthorityScan'),
  'Loading authority scanner...'
);

// NotFound
export const LazyNotFound = lazyRoute(
  () => import('@/pages/NotFound'),
  'Page not found...'
);
